import { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE } from "./config";

/**
 * Site-wide Organization schema. Rendered once, on the homepage only.
 * No `sameAs`/`address` — Footer.jsx's social links are placeholder URLs
 * (generic github.com/twitter.com/instagram.com, not real Teco accounts)
 * and there's no verified public business address to publish.
 */
export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
  };
}

/**
 * Site-wide WebSite schema. No `potentialAction`/SearchAction — the app has
 * no real site-search feature to point one at.
 */
export function buildWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
  };
}

/**
 * Shared shape behind buildFoodEstablishmentSchema/buildLiquorStoreSchema.
 * `aggregateRating` is only included when there's at least one real rating —
 * never fabricated, and only for stats already rendered on the page
 * (StoreInfo.jsx/LiquorStoreInfo.jsx's RatingCard).
 * @param {Object} params
 * @param {string} params.type - schema.org type, e.g. "FoodEstablishment" | "LiquorStore"
 * @param {string} params.name
 * @param {string} params.url - canonical URL
 * @param {string} [params.image]
 * @param {string} [params.cuisine] - only meaningful for FoodEstablishment
 * @param {{average_rating?: number, total_no_ratings?: number}} [params.ratingStats]
 */
function buildStoreSchema({ type, name, url, image, cuisine, ratingStats }) {
  const schema = { "@context": "https://schema.org", "@type": type, name, url };
  if (image) schema.image = image;
  if (cuisine) schema.servesCuisine = cuisine;
  if (ratingStats && ratingStats.total_no_ratings > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(ratingStats.average_rating.toFixed(1)),
      reviewCount: ratingStats.total_no_ratings,
    };
  }
  return schema;
}

/** @param {{name:string, url:string, image?:string, cuisine?:string, ratingStats?:Object}} params */
export function buildFoodEstablishmentSchema(params) {
  return buildStoreSchema({ ...params, type: "FoodEstablishment" });
}

/** @param {{name:string, url:string, image?:string, ratingStats?:Object}} params */
export function buildLiquorStoreSchema(params) {
  return buildStoreSchema({ ...params, type: "LiquorStore" });
}

/**
 * @param {Array<{name: string, url: string}>} items - in order, Home first
 */
export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
